import { Component } from 'preact';
import { toast } from 'sonner';
import StoreCosmeticsTab from './StoreCosmeticsTab';
import { setSavedPlaymatTheme } from './PlaymatPicker';
import { claimStarterPlaymat } from '../utils/arena/cosmeticsApi';
import {
  invalidateCosmeticsCache,
  setCurrentUserId as setCosmeticsUserId,
  getCosmeticsCached,
  awaitProtectedRegistry,
} from '../utils/arena/cosmeticsCache';
import { playUI, UI } from '../utils/arena/uiSounds';
import {
  GOLD, PARCHMENT, BG_ATMOSPHERE, VIGNETTE, CONTENT_BG_DEFAULT, OrnamentalDivider,
} from '../lib/medievalTheme';
import { t } from '../localization/t';

// First-run free playmat pick. Slots into onboarding right after the
// starter deck and before the Discord prompt. Reuses the store's playmat
// grid + sidebar (StoreCosmeticsTab in `starterPick` mode) so the artist
// seals, lore and "About the Artist" panel render identically. Wrapped
// in the ArenaStarterPicker chrome (textured/depth backdrop, page title)
// so it reads as part of the new-user flow, not the store.
export default class ArenaFirstPlaymatPicker extends Component {
  state = { claiming: false };

  // Belt-and-suspenders registry prime. The protected (Collector
  // Arthouse / OG-artist) mats load from /game-assets/protected-playmat/
  // <id>, which 404s `not-registered` until the registry PUT lands. The
  // app warms this at login (well before onboarding reaches this
  // screen), so the four previews are normally already cached here. This
  // fire-and-forget prime just guarantees the registry is primed even if
  // that path was skipped. It does NOT gate the UI (no blank): the grid
  // paints immediately, and PlaymatPreview retries any preview that
  // raced ahead of the PUT, so a stray early request never sticks as
  // "Artwork unavailable." The bundled native two need no registry.
  componentDidMount() {
    const id = this.props.profile?.id;
    if (!id) return;
    setCosmeticsUserId(id);
    getCosmeticsCached()
      .then(() => awaitProtectedRegistry())
      .catch(() => {});
  }

  handleChoose = async (pack) => {
    if (this.state.claiming) return;
    this.setState({ claiming: true });
    playUI(UI.GOLD);
    try {
      // cosmeticId is null for the free Vanilla mat (no entitlement to
      // grant); the server still consumes the one-time free pick.
      await claimStarterPlaymat(pack.cosmeticId || null);
      // Make the chosen mat the player's active one.
      setSavedPlaymatTheme(pack.themeId);
      // The new entitlement isn't in the cached cosmetics snapshot yet.
      // drop it so the next read reflects ownership.
      invalidateCosmeticsCache();
      playUI(UI.CONFIRM);
      this.props.onChosen?.(pack);
    } catch (err) {
      console.error('[FirstPlaymatPicker] claim failed:', err?.reason || err?.message);
      toast.error(t('Could not claim that playmat. Please try again.'));
      this.setState({ claiming: false });
    }
  };

  render() {
    const { sorceryCards, profile } = this.props;
    return (
      <div className="fixed inset-0 z-[55] flex flex-col" style={{ background: BG_ATMOSPHERE }}>
        {/* Stone/chisel texture wash + vignette. Same depth recipe as
            ArenaStarterPicker so the two onboarding steps feel of a piece. */}
        <div
          className="fixed inset-0 pointer-events-none"
          style={{
            background: `${CONTENT_BG_DEFAULT}, rgba(12, 10, 8, 0.6)`,
            backgroundBlendMode: 'overlay, normal',
          }}
        />
        <div className="fixed inset-0 pointer-events-none" style={{ background: VIGNETTE }} />

        {/* Full-height content. The "Choose Your Playmat" header is
            passed into the grid column (left) via `starterHeader` so the
            sidebar (right) keeps the full panel height and reads exactly
            like the store. No shrinking, no clipped lore/bio. */}
        <div className="flex-1 relative z-10 min-h-0 flex flex-col">
          <StoreCosmeticsTab
            starterPick
            busy={this.state.claiming}
            sorceryCards={sorceryCards}
            profile={profile}
            onStarterPick={this.handleChoose}
            starterHeader={(
              <div className="text-center pt-2 pb-3">
                <h1
                  className="arena-heading"
                  style={{
                    fontSize: '2.8cqw',
                    fontWeight: 700,
                    letterSpacing: '0.04em',
                    lineHeight: 1.05,
                    color: `${PARCHMENT} 0.96)`,
                    textShadow: [
                      '0 2px 6px rgba(0,0,0,0.85)',
                      `0 0 24px ${GOLD} 0.28)`,
                      '0 1px 0 rgba(255, 235, 195, 0.12)',
                    ].join(', '),
                    marginBottom: '0.3cqw',
                  }}
                >
                  {t('Choose Your Playmat')}
                </h1>
                <OrnamentalDivider className="my-1 w-64 mx-auto" />
                <p className="text-xs mt-1" style={{ color: `${PARCHMENT} 0.55)`, letterSpacing: '0.04em' }}>
                  {t("Pick the realm you'll command. Battle other players to earn gold and unlock more.")}
                </p>
              </div>
            )}
          />
        </div>
      </div>
    );
  }
}
